// src/components/email/PortalRecipientsModal.jsx
// A small confirm-recipients modal shown before a portal / forms send goes
// out. Every client on the file who is REACHABLE (email or phone) is listed
// and PRE-CHECKED; the agent can uncheck anyone. onSend receives the selected
// recipient OBJECTS ({contactId, name, email, phone}) — not just emails — so
// callers can email the emailable and text the textable. Shared by the lead
// drawers' "Send Forms" buttons and the deal-detail Client Portal panel.
import { useState } from "react";
import { X, Mail } from "lucide-react";

const rowKey = (r) => String(r.contactId || r.email || r.name || "");

const CHANNELS = [
  { key: "both", label: "Email + text" },
  { key: "email", label: "Email only" },
  { key: "text", label: "Text only" },
];

export default function PortalRecipientsModal({
  title = "Send forms",
  recipients = [],
  busy = false,
  // Callers that can only email (e.g. the deal-detail portal panel) pass
  // false and the channel picker is hidden.
  allowText = true,
  onCancel,
  onSend,
}) {
  const [channel, setChannel] = useState(allowText ? "both" : "email");
  // "Reachable" depends on the chosen channel: email-only sends need an
  // email, text-only sends need a phone, both needs either.
  const canReach = (r) =>
    channel === "email" ? !!r.email : channel === "text" ? !!r.phone : !!(r.email || r.phone);
  const reachable = recipients.filter(canReach);
  const unreachable = recipients.filter((r) => !canReach(r));
  const [checked, setChecked] = useState(
    () => new Set(recipients.filter((r) => r.email || r.phone).map((r) => rowKey(r))),
  );
  const toggle = (key) =>
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const selected = reachable.filter((r) => checked.has(rowKey(r)));

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-sm flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <h3 className="font-semibold text-slate-900 text-sm">{title}</h3>
          <button onClick={onCancel} className="text-slate-400 hover:text-slate-700">
            <X size={16} />
          </button>
        </div>

        <div className="p-4 space-y-2 max-h-[50vh] overflow-y-auto">
          {allowText && (
            <div className="flex gap-1.5 mb-1">
              {CHANNELS.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => setChannel(c.key)}
                  className={`px-2.5 py-1 text-xs font-semibold rounded-full border ${
                    channel === c.key
                      ? "bg-slate-900 text-white border-slate-900"
                      : "border-slate-300 text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          )}
          <p className="text-xs text-slate-500">
            Everyone on the file is selected. Uncheck anyone you don't want to
            send to.
          </p>
          {reachable.length === 0 ? (
            <p className="text-sm text-slate-400">
              {channel === "text"
                ? "No client on this file has a phone on record. Add one in Client Information, then try again."
                : channel === "email"
                  ? "No client on this file has an email on record. Add one in Client Information, then try again."
                  : "No client on this file has an email or phone on record. Add one in Client Information, then try again."}
            </p>
          ) : (
            reachable.map((r) => (
              <label
                key={rowKey(r)}
                className="flex items-center gap-2.5 p-2 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-50"
              >
                <input
                  type="checkbox"
                  checked={checked.has(rowKey(r))}
                  onChange={() => toggle(rowKey(r))}
                />
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-slate-800 truncate">
                    {r.name || "(no name)"}
                  </span>
                  <span className="block text-xs text-slate-500 truncate">
                    {channel === "text"
                      ? `Text · ${r.phone}`
                      : channel === "email"
                        ? r.email
                        : [
                            r.email,
                            r.phone ? `text ${r.phone}` : "",
                          ]
                            .filter(Boolean)
                            .join(" · ") || `Text only · ${r.phone}`}
                  </span>
                </span>
              </label>
            ))
          )}
          {unreachable.length > 0 && (
            <p className="text-[11px] text-slate-400">
              Skipped (no{" "}
              {channel === "text"
                ? "phone"
                : channel === "email"
                  ? "email"
                  : "email or phone"}{" "}
              on file): {unreachable.map((r) => r.name || "?").join(", ")}
            </p>
          )}
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200">
          <button
            onClick={onCancel}
            className="px-3 py-1.5 text-sm rounded-lg border border-slate-300 text-slate-600"
          >
            Cancel
          </button>
          <button
            onClick={() => onSend(selected, channel)}
            disabled={busy || selected.length === 0}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm rounded-lg bg-[#1E293B] text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Mail size={14} />
            {busy ? "Sending…" : `Send to ${selected.length}`}
          </button>
        </div>
      </div>
    </div>
  );
}
